import Jumbotron from '@/components/core/Jumbotron/Jumbotron';
import { Layout } from '@/components/shared/layouts/Layout/Layout';
import { ColorTypeOptions } from '@/components/shared/navigations/CoreNavigation/CoreNavigation';
import { getSafeLocale } from '@/utils/locale';
import { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import styles from '../../../styles/museum-details.module.scss';
import { Trans, useTranslation } from 'next-i18next';
import Link from 'next/link';
import Section from '@/components/core/Section/Section';
import Heading from '@/components/core/Heading/Heading';
import SectionColumn from '@/components/core/Section/SectionColumn/SectionColumn';
import FeaturedItem from '@/components/features/visit/FeaturedItem/FeaturedItem';
import SectionItem from '@/components/core/Section/SectionItem/SectionItem';
import { SHOP_LINK } from '@/utils/constants';
import { NextSeo } from 'next-seo';

type MuseumDetailsProps = InferGetServerSidePropsType<typeof getServerSideProps>;

const MuseumDetails: NextPage<MuseumDetailsProps> = () => {
	const { t } = useTranslation();

	return (
		<>
			<NextSeo title={t('zagreb-museum-details')} />
			<Layout colorTypeActive={ColorTypeOptions.PRIMARY}>
				<Jumbotron
					backgroundImage={'/images/museum-details/header.jpg'}
					contentClassName={styles.museumDetailsJumbotron}
					color={Jumbotron.color.PRIMARY}
				>
					<div className={styles.header}>
						<div className={styles.headerLeft}>
							<div className={styles.headerLeftTitle}>
								<Link href={'/visit'} className={styles.link}>
									<span className={styles.underline}>{t('visit')}</span>&#x2192; Zagreb
								</Link>
							</div>
							<p>{t('the-museum-is-permanently-loca')}</p>
							{/* <Button color={Button.color.HOLLOW}>Buy tickets</Button> */}
						</div>
						<div className={styles.headerRight}>
							<div className={styles.headerRightTitle}></div>
							<p>
								{t('zgmuseum.address')}
								<br />
								{t('zgmuseum.summer.title')}
								<br />
								<span className={styles.mobile}>
									<Trans i18nKey="zgmuseum.summer.mobile" />
								</span>
								<span className={styles.notMobile}>
									{t('zgmuseum.summer.notMobile')}
									<br />
								</span>
								{t('zgmuseum.winter.title')}
								<br />
								<span className={styles.mobile}>
									<Trans i18nKey="zgmuseum.winter.mobile" />
								</span>
								<span className={styles.notMobile}>{t('zgmuseum.winter.notMobile')}</span>
							</p>
							<p>
								{t('zgmuseum.email')}
								<br />
								{t('zgmuseum.phone')}
							</p>
						</div>
					</div>
				</Jumbotron>
				<Section>
					<Heading text={t('museum-of-broken-relationships-zg')} />
					<SectionColumn showTitle={false}>
						<FeaturedItem
							color={FeaturedItem.color.HIDDEN}
							align={FeaturedItem.align.LEFT}
							image={['/images/museum-details/museum.png', '/images/museum-details/museum@2x.png']}
							tilt={false}
						/>
					</SectionColumn>
					<SectionColumn showTitle={false}>
						<FeaturedItem
							color={FeaturedItem.color.PRIMARY}
							align={FeaturedItem.align.RIGHT}
							location={FeaturedItem.location.BOTTOM_LEFT}
							image={['/images/museum-details/staticmap-zg.png']}
						>
							<p className={styles.mapTitle}>Ćirilometodska 2, 10000, Zagreb</p>
							<a
								target="_blank"
								rel="noopener"
								href="https://www.google.hr/maps/place/Museum+of+Broken+Relationships/@45.8079356,15.9888492,15z/data=!4m5!3m4!1s0x0000000000000000:0x086f5272433861a8!8m2!3d45.8149222!4d15.9734344?hl=en"
								className={styles.mapsLink}
							>
								{t('open-in-google-maps')}
							</a>
						</FeaturedItem>
					</SectionColumn>
				</Section>
				<Section color="Primary">
					<SectionColumn title={t('hours--admission')}>
						<SectionItem title={t('working-hours')} icon="/assets/icons/clock-primary.svg">
							<p>
								<b>{t('summer')}</b> {t('june-1st--september-30th')}
								<br />
								{t('every-day-from-0900-am-to-1')}
								<br />
								{t('last-entry-930-pm')}
							</p>

							<p>
								<b>{t('winter')}</b> {t('october-1st--may-31st')}
								<br />
								{t('winter-hours-zg')}
								<br />
								{t('last-entry-830-pm')}
							</p>

							<p>
								<b>{t('closed')}</b> {t('on-christmas-eve-christmas-n')}
								<br />
								<b>{t('on-new-years-eve-open-from-10')}</b>
							</p>
						</SectionItem>

						<SectionItem title={t('display-language')} icon="/assets/icons/globe-primary.svg">
							<p>
								{t('museum-captions-are-in-croatia')}
								<br />
								{t('translations-are-available-in')}
							</p>
						</SectionItem>
						<SectionItem title={t('admissions')} icon="/assets/icons/ticket-primary.svg">
							<p>{t('adults-price')}</p>
							<p>{t('pupils-students-fulltime-wi')}</p>
							<p>{t('groups-of-15-persons-or-more')}</p>
							<p>
								{`${t('groups-of-15-people-or-more-mu')} `}
								<a href="mailto:info@brokenships.com">info@brokenships.com</a>.
							</p>
						</SectionItem>
					</SectionColumn>
					<SectionColumn title={t('getting-there')}>
						<SectionItem title={t('by-air')} icon="/assets/icons/by-air-primary.svg">
							<p>
								<a target="_blank" rel="noopener" href="http://www.zagreb-airport.hr/">
									{t('zagreb-international-airport')}
								</a>
								{t('pleso14km-southeast-of-the')}
							</p>
						</SectionItem>
						<SectionItem title={t('by-car')} icon="/assets/icons/by-car-primary.svg">
							<p>{t('feel-like-driving-within-croa')}</p>
							<p>{t('renting-a-car-in-croatia-is-a')}</p>
						</SectionItem>
						<SectionItem title={t('by-train')} icon="/assets/icons/by-train-primary.svg">
							<p>{t('it-may-not-be-glamorous-as-dur')}</p>
						</SectionItem>
						<SectionItem title={t('by-bus')} icon={'/assets/icons/by-bus-primary.svg'}>
							<p>{t('relatively-cheap-and-relativel')}</p>
						</SectionItem>
					</SectionColumn>
				</Section>
				<Section color="Dimmed">
					<SectionColumn className={styles.expandable} title={t('brokenships-caf')}>
						<SectionItem title={t('caf')} icon="/assets/icons/cafe-dimmed.svg">
							<p>
								<Trans i18nKey="museum-details-cafe-description" />
							</p>
						</SectionItem>
						<SectionItem title={t('working-hours')} icon="/assets/icons/clock-dimmed.svg">
							<p>
								{t('monday-to-friday-from-800-am')}
								<br />
								{t('saturday-and-sunday-from-900')}
							</p>
						</SectionItem>
						<SectionItem title={t('we-accept')} icon="/assets/icons/checked-dimmed.svg">
							<p>
								<img className={styles.bankCard} src="/assets/icons/card-mastercard.svg" />
								<img className={styles.bankCard} src="/assets/icons/card-maestro.svg" />
								<img className={styles.bankCard} src="/assets/icons/card-visa.svg" />
								<img className={styles.bankCard} src="/assets/icons/card-diners.svg" />
							</p>
						</SectionItem>
					</SectionColumn>
					<SectionColumn className={styles.collapsableLast}>
						<FeaturedItem
							color={FeaturedItem.color.HIDDEN}
							align={FeaturedItem.align.CENTER}
							image={['/images/museum-details/cafe.png', '/images/museum-details/cafe@2x.png']}
							imageClassName={styles.staticImage}
							tilt
						/>
					</SectionColumn>
				</Section>
				<Section color="Dimmed">
					<SectionColumn className={styles.collapsable} title={t('brokenships-museum-shop')}>
						<FeaturedItem
							color={FeaturedItem.color.HIDDEN}
							align={FeaturedItem.align.CENTER}
							image={['/images/museum-details/shop.png', '/images/museum-details/shop@2x.png']}
							imageClassName={styles.staticImage}
							tilt
						/>
					</SectionColumn>
					<SectionColumn className={styles.expandableLast} title="">
						<SectionItem title={t('shop')} icon="/assets/icons/shop-cart-dimmed.svg" iconLink={SHOP_LINK}>
							<p>{t('brokenships-gift-shop-offers-s')}</p>
							<p>{t('bad-memories-eraser-antistre')}</p>
							<p>{t('leave-the-bad-things-behind-g')}</p>
						</SectionItem>
						<SectionItem title={t('working-hours')} icon="/assets/icons/clock-dimmed.svg">
							<p>
								{t('summerjune-1st--september-30')}
								<br />
								{t('every-day-from-900-am-to-10')}
							</p>
							<p>
								{t('winteroctober-1st--may-31st')}
								<br />
								{t('every-day-from-1000-am-to-9')}
							</p>
							<p>{t('closed-on-december-24th-chris')}</p>
						</SectionItem>
						<SectionItem title={t('we-accept')} icon="/assets/icons/checked-dimmed.svg">
							<p>
								<img className={styles.bankCard} src="/assets/icons/card-mastercard.svg" />
								<img className={styles.bankCard} src="/assets/icons/card-maestro.svg" />
								<img className={styles.bankCard} src="/assets/icons/card-visa.svg" />
								<img className={styles.bankCard} src="/assets/icons/card-diners.svg" />
							</p>
						</SectionItem>
					</SectionColumn>
				</Section>
			</Layout>
		</>
	);
};

export const getServerSideProps: GetServerSideProps = async ({ locale }) => {
	return {
		props: {
			...(await serverSideTranslations(getSafeLocale(locale), ['common'])),
		},
	};
};

export default MuseumDetails;
